import { z } from "zod";

/**
 * Campaign budget history (ADP-222) — backs the Budget Tab 「预算变化趋势图」.
 *
 * Only observed budgets are returned: days before `trackedSince` (when AdPilot
 * started recording this campaign's budget) have `dailyBudget: null` rather
 * than a value inferred from today's budget.
 */
export const campaignBudgetChangeSourceSchema = z.enum([
  "baseline",
  "user_settings",
  "structure_sync",
]);
export type CampaignBudgetChangeSource = z.infer<typeof campaignBudgetChangeSourceSchema>;

const budgetTypeSchema = z.enum(["daily", "monthly"]);

export const campaignBudgetHistoryPointSchema = z.object({
  /** Marketplace-local day (yyyy-mm-dd). */
  date: z.string().date(),
  /** Budget in force at the END of the day; null before tracking or for a non-daily cap. */
  dailyBudget: z.number().nullable(),
  budgetType: budgetTypeSchema.nullable(),
});
export type CampaignBudgetHistoryPoint = z.infer<typeof campaignBudgetHistoryPointSchema>;

export const campaignBudgetChangeSchema = z.object({
  occurredAt: z.string().datetime({ offset: true }),
  oldDailyBudget: z.number().nullable(),
  newDailyBudget: z.number().nullable(),
  oldBudgetType: budgetTypeSchema.nullable(),
  newBudgetType: budgetTypeSchema.nullable(),
  /** `user_settings` = changed in AdPilot; `structure_sync` = changed elsewhere, seen at the next sync. */
  source: campaignBudgetChangeSourceSchema.exclude(["baseline"]),
});
export type CampaignBudgetChange = z.infer<typeof campaignBudgetChangeSchema>;

export const campaignBudgetHistorySchema = z.object({
  /** Marketplace time zone the days are cut in; null → UTC fallback. */
  timeZone: z.string().nullable(),
  currency: z.string().nullable(),
  /** First marketplace-local day with a known budget; null when never tracked. */
  trackedSince: z.string().date().nullable(),
  points: z.array(campaignBudgetHistoryPointSchema),
  /** Changes inside the window, oldest first (baselines excluded). */
  changes: z.array(campaignBudgetChangeSchema),
});
export type CampaignBudgetHistory = z.infer<typeof campaignBudgetHistorySchema>;

export const campaignBudgetHistoryResponseSchema = z.object({
  success: z.literal(true),
  data: campaignBudgetHistorySchema,
});
